import React, { useState, useContext, useEffect, useCallback } from 'react'
import {
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  BreadCrumble,
  Button,
  Card,
  CollapsibleHeader,
  FirstText,
  FormGrid,
  FormGroup,
  InfoLink,
  InfoName,
  InfoValue,
  InputText,
  Label,
  MoreInformation,
  SecondText,
  Select,
  Separator,
  ShortLabel,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  Title,
} from './style'
import { ChevronDown, ChevronUp, Info, Plus } from 'lucide-react'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import AddProductForm from '../AddProductForm'
import { useOutletContext } from 'react-router-dom'
import axios from '../../../../config/axios'
import { ActionsDiv, ProductDiv, ProductShort } from '../AddProductForm/style'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'
import LoadingComponent from '../../../../components/LoadingComponent'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import AttachmentManager from '../../../../components/AttachmentManager'

import editPen from '../../../../assets/fashion/TableFashionHeader/edit-pen.svg' // Importar para o resumo do produto
import VendorSelect from '../SelectItem/VendorSelect'
import CompanySelect from '../SelectItem/CompanySelect'

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      resolve(result)
    }
    reader.onerror = (error) => reject(error)
  })
}

interface FamilyData {
  id: number
  name: string
  groups?: GroupData[]
  colors?: { name: string }[]
}

interface GroupData {
  id: number
  name: string
  products?: any[]
}

interface FashionOrderProductItemInput {
  colorId: number
  size: string
  quantity: number
}

interface ProductApiData {
  fashionProductId: number
  discount: number | null
  totalItems: number
  subtotal: number
  items: FashionOrderProductItemInput[]
  fashionOrderId?: number
  fashionProduct?: {
    id: number
    name: string
    price: number
    size: string[]
    group: {
      id: number
      name?: string
      family?: {
        id: number
        name?: string
        collectionId?: number
      }
    }
  }
  colorCount?: number
  colors?: string[]
  family?: {
    id: number
    Corileone?: { name: string }
    name?: string
    familyId?: number
  }
  group?: { id: number; name?: string }
  name?: string
  price?: number
  totalQuantity?: number
  totalSubtotal?: number
  selectedColors?: number[]
  discountValue?: number
}

interface CompanyData {
  id: number
  companyName: string
}

interface CompanyItemType {
  id: number
  companyName: string
}

interface UserItemType {
  id: number;
  name: string;
}

interface CreateFashionOrderPayload {
  companyId: number;
  supplierId: number;
  sellerId: number;
  orderType: string;
  paymentCondition: string;
  shippingType: string;
  trackingCode: string;
  shippingCost: number;
  totalCost: number;
  startDateOfDelivery: string | null;
  finalDateOfDelivery: string | null;
  extraInformation: string;
  quantityItems: string;
  quantityPieces: string;
  deliveryTime: string;
  status: string;
  saleStatus: string;
  maximumBillingAmount: string;
  products: {
    fashionProductId?: number;
    groupId?: number;
    discount: number | null;
    totalItems: number;
    subtotal: number;
    items: {
      colorId: number;
      size: string;
      quantity: number;
    }[];
    colorCount?: number;
    colors?: string[];
    selectedColors?: number[];
  }[];
  attachments?: {
    file: string;
    fileName: string;
  }[];
}

type FashionFormContent = {
  handleSave: React.MutableRefObject<() => void>
}

const cleanCurrency = (value: any): number => {
  const stringValue = String(value || '')
  const number = Number(stringValue.replace(/[^0-9,-]+/g, '').replace(',', '.'))
  return isNaN(number) ? 0 : number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatForInput = (digits: string): string => {
  if (digits === null || digits === undefined || digits === '') {
    return ''
  }

  const numericValue = Number(digits) / 100

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numericValue);
}

const calculateDiscountValue = (subtotal: number, discountPercentage: number): number => {
  return (subtotal * discountPercentage) / 100;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const AddFashionOrder: React.FC = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [isInformationOpen, setIsInformationOpen] = useState(false)
  const [isClientInformationOpen, setIsClientInformationOpen] = useState(false)
  const [isSellerInfoOpen, setIsSellerInfoOpen] = useState(false)
  const [companyData, setCompanyData] = useState<CompanyData[]>([])
  const [supplierData, setSupplierData] = useState<CompanyData[]>([])
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(
    null,
  )
  const [productToEdit, setProductToEdit] = useState<ProductApiData | null>(
    null,
  )
  const [shippingOptions, setShippingOptions] = useState<string[]>([])
  const [newshippingType, setNewshippingType] = useState('')
  const [paymentOptions, setPaymentOptions] = useState<string[]>([])
  const [newPaymentCondition, setNewPaymentCondition] = useState('')
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [isAddingShipping, setIsAddingShipping] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [fileUploadStatus, setFileUploadStatus] = useState<string>("")

  const { setFeedback } = useContext(FeedbackContext)

  const { handleSave, setIsSaving: setParentSaving } = useOutletContext<FashionFormContent & { setIsSaving?: (val: boolean) => void }>()
  const [formState, setFormState] = useState({
    supplier: { id: 0, companyName: '' } as CompanyItemType,
    seller: { id: 0, name: '' } as UserItemType,
    company: { id: 0, companyName: '' } as CompanyItemType,
    orderType: '',
    paymentCondition: '',
    shippingType: '',
    trackingCode: '',
    attachment: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '0',
    quantityPieces: '0',
    totalCost: 'R$ 0,00',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024
    const validFiles: File[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`Arquivo ${file.name} excede 200MB e será ignorado.`)
        continue
      }
      validFiles.push(file)
    }

    setSelectedFiles(prev => [...prev, ...validFiles])
    setFileUploadStatus("")
    event.target.value = ""
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const handleAddshippingType = async () => {
    if (newshippingType && !shippingOptions.includes(newshippingType)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`,
          {
            name: newshippingType,
          },
        )
        if (response.status === 201) {
          setShippingOptions([...shippingOptions, newshippingType])
          setFormState({ ...formState, shippingType: newshippingType })
          setNewshippingType('')
          setIsAddingShipping(false)
        } else {
          console.error(
            'Erro ao salvar o novo tipo de frete:',
            response.statusText,
          )
          alert('Erro ao salvar o novo tipo de frete. Tente novamente.')
        }
      } catch (error) {
        console.error('Erro ao conectar com o backend:', error)
        alert(
          'Não foi possível conectar com o servidor para salvar o novo tipo de frete.',
        )
      }
    }
  }

  const handleAddPaymentCondition = async () => {
    if (newPaymentCondition && !paymentOptions.includes(newPaymentCondition)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`,
          { name: newPaymentCondition },
        )

        if (response.status === 201) {
          setPaymentOptions([...paymentOptions, newPaymentCondition])
          setFormState({ ...formState, paymentCondition: newPaymentCondition })
          setNewPaymentCondition('')
          setIsAddingPayment(false)
        } else {
          console.error(
            'Erro ao salvar a nova condição de pagamento:',
            response.statusText,
          )
          alert('Erro ao salvar a nova condição de pagamento. Tente novamente.')
        }
      } catch (error) {
        console.error('Erro ao conectar com o backend:', error)
        alert(
          'Não foi possível conectar com o servidor para salvar a nova condição de pagamento.',
        )
      }
    }
  }

  const handleRemoveProductParent = (indexToRemove: number) => {
    setAddedProducts((prevProducts) => {
      const newProducts = [...prevProducts]
      newProducts.splice(indexToRemove, 1)
      return newProducts
    })
    setEditingProductIndex(null)
    setProductToEdit(null)
  }

  const handleEditProduct = (product: ProductApiData, index: number) => {
    setProductToEdit(product)
    setEditingProductIndex(index)
    setShowAddProduct(true)
  }

  const handleProductUpdated = (
    oldProduct: ProductApiData,
    updatedProduct: ProductApiData,
  ) => {
    if (editingProductIndex !== null) {
      const productWithColorCount = {
        ...updatedProduct,
        colorCount:
          updatedProduct.colorCount ||
          (updatedProduct.items && Array.isArray(updatedProduct.items)
            ? new Set(updatedProduct.items.map((item) => item.colorId)).size
            : 0),
      }

      setAddedProducts((prevProducts) => {
        const newProducts = [...prevProducts]
        const existingProduct = newProducts[editingProductIndex]
        newProducts[editingProductIndex] = {
          ...existingProduct,
          ...productWithColorCount,
          items: productWithColorCount.items || existingProduct.items || [],
          totalItems:
            productWithColorCount.totalItems ||
            (productWithColorCount.items
              ? productWithColorCount.items.reduce(
                (sum, item) => sum + (item.quantity || 0),
                0,
              )
              : existingProduct.totalItems),
          subtotal: productWithColorCount.subtotal || existingProduct.subtotal,
        }

        return newProducts
      })

      setProductToEdit(null)
      setEditingProductIndex(null)
      setShowAddProduct(false)
    }
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    const productWithColorCount = {
      ...newProduct,
      colorCount:
        newProduct.colorCount ||
        (newProduct.items && Array.isArray(newProduct.items)
          ? new Set(newProduct.items.map((item) => item.colorId)).size
          : 0),
    }

    setAddedProducts((prevProducts) => [...prevProducts, productWithColorCount])
    setShowAddProduct(false)
  }

  const handleSupplierChange = (supplier: CompanyItemType) => {
    setFormState((prevState) => ({ ...prevState, supplier }))
  }

  const handleCompanyChange = (company: CompanyItemType) => {
    setFormState((prevState) => ({ ...prevState, company }))
  }

  const handleSellerChange = (seller: UserItemType) => {
    setFormState((prevState) => ({ ...prevState, seller }))
  }

  const handleSaveOrder = useCallback(async () => {
    try {
      if (setParentSaving) setParentSaving(true)
      const groupedProductsMap = new Map<number, ProductApiData>()

      addedProducts.forEach((product) => {
        const productId = product.fashionProduct?.id || product.fashionProductId

        if (productId === undefined) {
          console.error('Produto sem ID, ignorando...')
          return
        }

        if (groupedProductsMap.has(productId)) {
          const existingProduct = groupedProductsMap.get(productId)
          if (existingProduct) {
            existingProduct.items = [
              ...(existingProduct.items || []),
              ...(product.items || []),
            ]
            existingProduct.totalItems += product.totalItems || 0
            existingProduct.subtotal += product.subtotal || 0
          }
        } else {
          groupedProductsMap.set(productId, { ...product })
        }
      })

      const productsToSend = Array.from(groupedProductsMap.values())

      const formattedStartDate = formState.startDateOfDelivery
        ? `${formState.startDateOfDelivery}T00:00:00.000Z`
        : null
      const formattedFinalDate = formState.finalDateOfDelivery
        ? `${formState.finalDateOfDelivery}T00:00:00.000Z`
        : null

      console.log('🔍 [CREATE-ORDER] FormState atual:', {
        company: formState.company,
        supplier: formState.supplier,
        seller: formState.seller,
        companyId: formState.company.id,
        supplierId: formState.supplier.id,
        sellerId: formState.seller.id
      });

      const payload: CreateFashionOrderPayload = {
        companyId: formState.company.id,
        supplierId: formState.supplier.id,
        sellerId: formState.seller.id,
        orderType: formState.orderType,
        paymentCondition: formState.paymentCondition,
        shippingType: formState.shippingType,
        trackingCode: formState.trackingCode,
        shippingCost: cleanCurrency(formState.shippingCost),
        totalCost: cleanCurrency(formState.totalCost),
        startDateOfDelivery: formattedStartDate,
        finalDateOfDelivery: formattedFinalDate,
        extraInformation: formState.extraInformation,
        quantityItems: formState.quantityItems,
        quantityPieces: formState.quantityPieces,
        deliveryTime: formState.deliveryTime,
        status: "Em orçamento",
        saleStatus: "Em andamento",
        maximumBillingAmount: formState.maximumBillingAmount,
        products: productsToSend.map((product) => {
          let itemsToSend = product.items || []

          if (
            (!itemsToSend || itemsToSend.length === 0) &&
            product.colorCount &&
            product.colorCount > 0
          ) {
            if (
              product.selectedColors &&
              Array.isArray(product.selectedColors) &&
              product.selectedColors.length > 0
            ) {
              itemsToSend = product.selectedColors.map((colorId: any) => ({
                colorId: colorId,
                size: 'N/A',
                quantity: 0,
              }))
            } else if (
              product.colors &&
              Array.isArray(product.colors) &&
              product.colors.length > 0
            ) {
              itemsToSend = (product.colors as any[]).map(
                (colorInfo: any, index: number) => ({
                  colorId:
                    typeof colorInfo === 'object' ? colorInfo.id : index + 1,
                  size: 'N/A',
                  quantity: 0,
                }),
              )
            } else {
              itemsToSend = Array.from(
                { length: product.colorCount },
                (_, index) => ({
                  colorId: index + 1,
                  size: 'N/A',
                  quantity: 0,
                }),
              )
            }
          }

          return {
            fashionProductId:
              product.fashionProduct?.id || product.fashionProductId,
            groupId: product.fashionProduct?.group?.id || product.group?.id,
            discount: product.discount,
            totalItems: product.totalItems,
            subtotal: product.subtotal,
            items: itemsToSend,
            colorCount: product.colorCount,
            colors: product.colors,
            selectedColors: product.selectedColors,
          }
        }),
      }

      if (selectedFiles.length > 0) {
        try {
          setFileUploadStatus("Convertendo arquivos...")
          const attachmentsData = []

          for (const file of selectedFiles) {
            const base64Data = await fileToBase64(file)
            attachmentsData.push({
              file: base64Data,
              fileName: file.name
            })
          }

          payload.attachments = attachmentsData
          console.log(`📦 [CREATE-ORDER] Enviando ${attachmentsData.length} anexo(s):`, attachmentsData.map(a => ({ fileName: a.fileName, size: a.file.length })))
          setFileUploadStatus("Arquivos processados, enviando pedido...")
        } catch (error) {
          console.error('Erro ao converter arquivos para base64:', error)
          setFileUploadStatus("Erro ao processar arquivos")
          alert('Erro ao processar os arquivos anexos')
          return
        }
      } else {
        console.log('⚠️ [CREATE-ORDER] Nenhum anexo selecionado')
      }

      console.log('📤 [CREATE-ORDER] Enviando pedido:', {
        hasAttachments: !!payload.attachments,
        attachmentsCount: payload.attachments?.length || 0,
        productsCount: payload.products.length,
        companyId: payload.companyId,
        supplierId: payload.supplierId,
        sellerId: payload.sellerId
      });

      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/create-order`,
        payload
      );

      console.log('✅ [CREATE-ORDER] Pedido criado com sucesso:', response.data);
      
      setFeedback({
        isOpen: true,
        message: 'Pedido criado com sucesso!',
        statusOk: true
      })
      
      setSelectedFiles([]);
      setTimeout(() => setFileUploadStatus(""), 3000);

    } catch (error: any) {
      console.error('❌ [CREATE-ORDER] Erro ao salvar pedido:', error.response?.data || error.message);
      setFileUploadStatus("Erro ao salvar pedido")
      
      setFeedback({
        isOpen: true,
        message: `Erro ao salvar pedido: ${error.response?.data?.message || error.message}`,
        statusOk: false
      })
    } finally {
      if (setParentSaving) setParentSaving(false)
    }
  }, [formState, addedProducts, selectedFiles, setFeedback, setParentSaving]);

  useEffect(() => {
    console.log('⚙️ [ADD-ORDER] Configurando handleSave.current com handleSaveOrder')
    handleSave.current = handleSaveOrder
  }, [handleSaveOrder])

  useEffect(() => {
    const fetchShippingOptions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`,
        )
        if (response.status === 200) {
          const types = response.data.map((item: { name: string }) => item.name)
          setShippingOptions(types)
        }
      } catch (error) {
        console.error('Erro ao carregar os tipos de frete:', error)
      }
    }
    fetchShippingOptions()
  }, [])

  useEffect(() => {
    const fetchPaymentOptions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`,
        )
        if (response.status === 200) {
          const conditions = response.data.map(
            (item: { name: any }) => item.name,
          )
          setPaymentOptions(conditions)
        }
      } catch (error) {
        console.error('Erro ao carregar as condições de pagamento:', error)
      }
    }
    fetchPaymentOptions()
  }, [])

  useEffect(() => {
    let totalItemsCount = 0;
    let totalPiecesSum = 0;
    let totalCostSum = 0;
    let totalDiscountValue = 0;

    addedProducts.forEach(product => {
      totalItemsCount += 1;
      totalPiecesSum += Number(product.totalItems || 0);

      const subtotalWithoutDiscount = product.subtotal || 0;

      let discountValue = 0;
      if (product.discount && product.discount > 0) {
        const originalSubtotal = subtotalWithoutDiscount / (1 - product.discount / 100);
        discountValue = calculateDiscountValue(originalSubtotal, product.discount);
      }

      totalDiscountValue += discountValue;
      totalCostSum += subtotalWithoutDiscount;
    });

    setFormState((prevState) => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: formatCurrency(totalCostSum),
    }));
  }, [addedProducts])

  const renderProductSummary = (product: ProductApiData, index: number) => {
    let familyName = '-'
    if (product.fashionProduct?.group?.family?.name) {
      familyName = product.fashionProduct.group.family.name
    } else if (product.family?.name) {
      familyName = product.family.name
    }

    let groupName = '-'
    if (product.fashionProduct?.group?.name) {
      groupName = product.fashionProduct.group.name
    } else if (product.group?.name) {
      groupName = product.group.name
    }

    const numberOfColorsToDisplay = (() => {
      if (product.colorCount !== undefined && product.colorCount !== null && product.colorCount > 0) {
        return product.colorCount
      }

      if (product.items && Array.isArray(product.items) && product.items.length > 0) {
        const uniqueColorIds = new Set(product.items.map((item) => item.colorId))
        return uniqueColorIds.size
      }

      if (product.colors && Array.isArray(product.colors) && product.colors.length > 0) {
        return product.colors.length
      }

      return 0
    })()

    const getDiscountValue = (): string => {
      if (!product.discount || product.discount <= 0) {
        return "-";
      }

      if (product.discountValue !== undefined) {
        return formatCurrency(product.discountValue);
      }

      const subtotal = product.subtotal || 0;
      const originalSubtotal = subtotal / (1 - product.discount / 100);
      const discountValue = calculateDiscountValue(originalSubtotal, product.discount);

      return formatCurrency(discountValue);
    };

    const productFields = [
      {
        label: 'Produto',
        value: product.fashionProduct?.name ?? '-',
        showIcon: true,
      },
      { label: 'Coleção', value: familyName, showIcon: true },
      { label: 'Cores', value: numberOfColorsToDisplay, showIcon: true },
      {
        label: 'Precificação',
        value:
          typeof product.fashionProduct?.price === 'number'
            ? `R$ ${product.fashionProduct.price.toFixed(2).replace('.', ',')}`
            : '-',
        showIcon: true,
      },
      {
        label: 'Quantidade de peças',
        value: product.totalItems ?? '-',
        showIcon: true,
      },
      {
        label: "Desconto",
        value: getDiscountValue(),
        showIcon: false,
      },
      {
        label: "Subtotal",
        value: typeof product.subtotal === "number" ? formatCurrency(product.subtotal) : "-",
        showIcon: false,
      },
    ]

    return (
      <Card key={product.fashionProductId || index}>
        <ProductShort>
          <ProductDiv>
            {productFields.map(({ label, value, showIcon }, i) => (
              <ShortLabel key={i}>
                <span style={{ fontWeight: 'normal' }}>{label}:</span> {value}
                {showIcon && (
                  <img
                    style={{ marginLeft: '0.5rem' }}
                    src={rectangle || '/placeholder.svg'}
                    alt=''
                  />
                )}
              </ShortLabel>
            ))}

            <ActionsDiv className='flex items-center gap-2'>
              <img
                src={trash || '/placeholder.svg'}
                alt='Remover'
                style={{ cursor: 'pointer' }}
                onClick={() => handleRemoveProductParent(index)}
              />
              <img
                src={editPen || '/placeholder.svg'}
                alt='Editar'
                style={{ cursor: 'pointer' }}
                onClick={() => handleEditProduct(product, index)}
              />
            </ActionsDiv>
          </ProductDiv>
        </ProductShort>
      </Card>
    )
  }

  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Novo pedido</SecondText>
      </BreadCrumble>

      <Card>
        <FormGrid>
          <FormGroup>
            <CompanySelect
              label='Fornecedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 2 }}
              value={formState.supplier}
              onChange={handleSupplierChange}
            />
            <InfoLink>
              <Info size={16} />
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span onClick={() => setIsInformationOpen(!isInformationOpen)}>
                  Mais informações
                </span>
                {isInformationOpen && formState.supplier.id > 0 && (
                  <MoreInformation>
                    <InfoName>
                      Razão Social: <InfoValue>{(formState.supplier as any).companyName || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      CNPJ/CPF: <InfoValue>{(formState.supplier as any).cnpj || (formState.supplier as any).cpf || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Cidade/UF: <InfoValue>{(formState.supplier as any).city?.name || 'N/A'}/{(formState.supplier as any).state?.name || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Telefone: <InfoValue>{(formState.supplier as any).contacts?.[0]?.phoneNumber || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Email: <InfoValue>{(formState.supplier as any).contacts?.[0]?.email || 'N/A'}</InfoValue>
                    </InfoName>
                  </MoreInformation>
                )}
              </div>
            </InfoLink>
          </FormGroup>
          <FormGroup>
            <VendorSelect
              label='Vendedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`}
              displayField='name'
              value={formState.seller}
              onChange={handleSellerChange}
            />
            <InfoLink>
              <Info size={16} />
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span onClick={() => setIsSellerInfoOpen(!isSellerInfoOpen)}>
                  Mais informações
                </span>
                {isSellerInfoOpen && formState.seller.id > 0 && (
                  <MoreInformation>
                    <InfoName>
                      Nome: <InfoValue>{(formState.seller as any).name || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Email: <InfoValue>{(formState.seller as any).email || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Perfil: <InfoValue>{(formState.seller as any).profile?.name || 'N/A'}</InfoValue>
                    </InfoName>
                  </MoreInformation>
                )}
              </div>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <CompanySelect
              label='Cliente'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
              displayField='companyName'
              queryParams={{ companyType: 1 }}
              value={formState.company}
              onChange={handleCompanyChange}
            />
            <InfoLink>
              <Info size={16} />
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span onClick={() => setIsClientInformationOpen(!isClientInformationOpen)}>
                  Mais informações
                </span>
                {isClientInformationOpen && formState.company.id > 0 && (
                  <MoreInformation>
                    <InfoName>
                      Razão Social: <InfoValue>{(formState.company as any).companyName || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      CNPJ/CPF: <InfoValue>{(formState.company as any).cnpj || (formState.company as any).cpf || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Cidade/UF: <InfoValue>{(formState.company as any).city?.name || 'N/A'}/{(formState.company as any).state?.name || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Telefone: <InfoValue>{(formState.company as any).contacts?.[0]?.phoneNumber || 'N/A'}</InfoValue>
                    </InfoName>
                    <InfoName>
                      Email: <InfoValue>{(formState.company as any).contacts?.[0]?.email || 'N/A'}</InfoValue>
                    </InfoName>
                  </MoreInformation>
                )}
              </div>
            </InfoLink>
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>
            {isDetailsOpen ? (
              <ChevronUp size={12} />
            ) : (
              <ChevronDown size={12} />
            )}
          </Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid style={{ marginTop: '16px' }}>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formState.orderType}
                  onChange={(e) =>
                    setFormState({ ...formState, orderType: e.target.value })
                  }
                >
                  <option value=''>Selecione</option>
                  <option value='Venda'>Venda</option>
                  <option value='Reposição'>Reposição</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <InputText
                  type='date'
                  value={formState.startDateOfDelivery}
                  max='9999-12-31'
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      startDateOfDelivery: e.target.value,
                    })
                  }
                  className='w-full rounded bg-gray-100 p-2'
                />
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText
                  type='date'
                  value={formState.finalDateOfDelivery}
                  max='9999-12-31'
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      finalDateOfDelivery: e.target.value,
                    })
                  }
                  className='w-full rounded bg-gray-100 p-2'
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <InfiniteSelect
                  label="Condição de pagamento"
                  placeholder="Selecione a condição de pagamento"
                  value={formState.paymentCondition ? { id: formState.paymentCondition, name: formState.paymentCondition } : null}
                  onSelect={(option) => {
                    setFormState({ ...formState, paymentCondition: option?.name || '' })
                  }}
                  mode="crud"
                  allowCreate={true}
                  allowDelete={true}
                  endpointConfig={{
                    baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
                    listPath: '/fashion-order/payment-conditions',
                    createPath: '/fashion-order/payment-conditions',
                    deletePath: '/fashion-order/payment-conditions',
                    searchParam: 'search',
                    limitParam: 'limit',
                    offsetParam: 'offset',
                    transformResponse: (data) => {
                      if (Array.isArray(data)) {
                        return data.map((item: any) => ({ id: item.id, name: item.name }));
                      }
                      return [];
                    },
                    transformCreateResponse: (data) => ({ id: data.id, name: data.name })
                  }}
                />
              </FormGroup>

              <FormGroup>
                <InfiniteSelect
                  label="Tipo de frete"
                  placeholder="Selecione o tipo de frete"
                  value={formState.shippingType ? { id: formState.shippingType, name: formState.shippingType } : null}
                  onSelect={(option) => {
                    setFormState({ ...formState, shippingType: option?.name || '' })
                  }}
                  mode="crud"
                  allowCreate={true}
                  allowDelete={true}
                  endpointConfig={{
                    baseUrl: `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders`,
                    listPath: '/fashion-order/shipping-types',
                    createPath: '/fashion-order/shipping-types',
                    deletePath: '/fashion-order/shipping-types',
                    searchParam: 'search',
                    limitParam: 'limit',
                    offsetParam: 'offset',
                    transformResponse: (data) => {
                      if (Array.isArray(data)) {
                        return data.map((item: any) => ({ id: item.id, name: item.name }));
                      }
                      return [];
                    },
                    transformCreateResponse: (data) => ({ id: data.id, name: data.name })
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <InputText
                  type='text'
                  value={formatForInput(formState.shippingCost)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '')
                    setFormState({ ...formState, shippingCost: cleanedValue })
                  }}
                  placeholder='Digite o valor do frete'
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <InputText
                  type='text'
                  value={formState.trackingCode}
                  onChange={(e) =>
                    setFormState({ ...formState, trackingCode: e.target.value })
                  }
                  placeholder='Digite o código de rastreio'
                />
              </FormGroup>

              <FormGroup>
                <Label>Anexos</Label>
                <AttachmentManager
                  selectedFiles={selectedFiles}
                  onFileChange={handleFileChange}
                  onRemoveFile={handleRemoveFile}
                  formatFileSize={formatFileSize}
                />
              </FormGroup>

              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText
                  type='text'
                  value={formatForInput(formState.maximumBillingAmount)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '')
                    setFormState({
                      ...formState,
                      maximumBillingAmount: cleanedValue,
                    })
                  }}
                  className='w-full rounded bg-gray-100 p-2'
                  placeholder='Digite o valor máximo'
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type='text'
                  value={formState.extraInformation}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      extraInformation: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>
          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formState.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formState.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formState.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>

      {Array.isArray(addedProducts) && addedProducts.length > 0 && (
        <>
          {addedProducts.map((product, index) =>
            renderProductSummary(product, index),
          )}
        </>
      )}

      <AddProductContainer>
        {showAddProduct && (
          <AddProductForm
            onRemoveProduct={handleRemoveProductParent}
            onProductAdded={
              editingProductIndex === null ? handleNewProductAdded : undefined
            }
            onProductUpdated={
              editingProductIndex !== null ? handleProductUpdated : undefined
            }
            product={productToEdit}
            isEditing={editingProductIndex !== null}
            onClose={() => {
              setShowAddProduct(false)
              setEditingProductIndex(null)
              setProductToEdit(null)
            }}
            order={[]}
            setOrder={function (value: any): void {
              throw new Error('Function not implemented.')
            }}
          />
        )}
        <AddButton
          onClick={() => {
            setEditingProductIndex(null)
            setProductToEdit(null)
            setShowAddProduct(true)
          }}
        >
          <AddButtonImg src={plus} />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton>
      </AddProductContainer>
    </>
  )
}

export default AddFashionOrder
